import { useEffect, useState } from "react";
import { AlertTriangle, Info, Lock, X } from "lucide-react";

import { Modal } from "@/components/Modal";
import { useApp, type LiveListing } from "@/context/AppContext";
import {
  getEbayListingState,
  reviseEbayListingPrice,
  type EbayListingState,
} from "@/features/ebayListings";

interface Props {
  listing: LiveListing | null;
  onClose: () => void;
  onSuccess: (text: string) => void;
}

interface PriceCaps {
  canEditStartingBid: boolean;
  canEditBin: boolean;
  canEditReserve: boolean;
  lockReason: string | null;
}

function computeCaps(state: EbayListingState | null): PriceCaps {
  if (!state) {
    return {
      canEditStartingBid: true,
      canEditBin: true,
      canEditReserve: true,
      lockReason: null,
    };
  }
  const hasBids = state.bidCount > 0;
  const endingSoon = state.endTime
    ? new Date(state.endTime).getTime() - Date.now() < 12 * 60 * 60 * 1000
    : false;
  const lockReason = hasBids
    ? `Has ${state.bidCount} bid${state.bidCount === 1 ? "" : "s"} — eBay won't let you change starting bid or Buy It Now`
    : endingSoon
      ? "Ends within 12 hours — eBay won't let you change starting bid or Buy It Now"
      : null;
  const canEditReserve = endingSoon
    ? false
    : hasBids
      ? state.reservePrice != null
      : true;
  return {
    canEditStartingBid: !hasBids && !endingSoon,
    canEditBin: !hasBids && !endingSoon,
    canEditReserve,
    lockReason,
  };
}

export function EditPriceModal({ listing, onClose, onSuccess }: Props) {
  const { updateLiveListing } = useApp();
  const open = listing !== null;

  const [state, setState] = useState<EbayListingState | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [bin, setBin] = useState("");
  const [startingBid, setStartingBid] = useState("");
  const [reserve, setReserve] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!listing) {
      setState(null);
      setLoading(false);
      setLoadError(null);
      setError(null);
      setBin("");
      setStartingBid("");
      setReserve("");
      return;
    }
    if (!listing.ebayListingId) {
      setLoadError("This listing has no eBay item ID — cannot revise price.");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setError(null);
    void getEbayListingState(listing.ebayListingId)
      .then((s) => {
        if (cancelled) return;
        setState(s);
        if (s.listingType === "Auction") {
          setStartingBid(
            (
              s.startPrice ??
              listing.startingBid ??
              listing.askingPrice ??
              0
            ).toFixed(2),
          );
          setReserve(
            (s.reservePrice ?? listing.reservePrice)
              ? (s.reservePrice ?? listing.reservePrice ?? 0).toFixed(2)
              : "",
          );
          setBin(
            (s.buyItNowPrice ?? listing.buyItNowPrice)
              ? (s.buyItNowPrice ?? listing.buyItNowPrice ?? 0).toFixed(2)
              : "",
          );
        } else {
          setBin((s.startPrice ?? listing.askingPrice).toFixed(2));
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(
          err instanceof Error ? err.message : "Could not load listing details",
        );
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [listing]);

  if (!listing) return null;

  const caps = computeCaps(state);
  const effectiveType = state?.listingType ?? listing.listingType;
  const isAuction = effectiveType === "Auction";

  const reserveInputError = (() => {
    if (!state || state.listingType !== "Auction" || state.bidCount === 0)
      return null;
    const t = reserve.trim();
    if (!t) return null;
    const n = Number(t);
    if (!Number.isFinite(n) || n <= 0) return null;
    const current = state.reservePrice;
    if (current == null) return null;
    if (n >= current) {
      return `Reserve can only be lowered (current $${current.toFixed(2)}) once bids exist`;
    }
    return null;
  })();

  const parseMoney = (s: string): number | undefined => {
    const t = s.trim();
    if (!t) return undefined;
    const n = Number(t);
    if (!Number.isFinite(n) || n <= 0) return NaN;
    return Math.round(n * 100) / 100;
  };

  const changed = (
    input: number | undefined,
    original: number | undefined,
  ): number | undefined => {
    if (input === undefined) return undefined;
    if (Number.isNaN(input)) return input;
    if (original !== undefined && Math.abs(input - original) < 0.005)
      return undefined;
    return input;
  };

  const submit = async () => {
    if (!listing?.ebayListingId) return;
    setError(null);

    let body: Parameters<typeof reviseEbayListingPrice>[0];
    let appliedSb: number | undefined;
    let appliedRp: number | undefined;
    let appliedBin: number | undefined;

    if (isAuction) {
      const sbRaw = parseMoney(startingBid);
      const rpRaw = parseMoney(reserve);
      const binRaw = parseMoney(bin);
      const sb = caps.canEditStartingBid
        ? changed(sbRaw, state?.startPrice ?? listing.startingBid)
        : undefined;
      const currentReserve = state?.reservePrice ?? listing.reservePrice;
      const rp = caps.canEditReserve
        ? changed(rpRaw, currentReserve)
        : undefined;
      if (
        rp !== undefined &&
        !Number.isNaN(rp) &&
        state &&
        state.bidCount > 0 &&
        currentReserve !== undefined &&
        rp >= currentReserve
      ) {
        setError(
          `Reserve can only be lowered (current $${currentReserve.toFixed(2)}) once the auction has bids`,
        );
        return;
      }
      const binChange = caps.canEditBin
        ? changed(binRaw, state?.buyItNowPrice ?? listing.buyItNowPrice)
        : undefined;
      if (sb === undefined && rp === undefined && binChange === undefined) {
        setError("Change at least one editable price field");
        return;
      }
      if (sb !== undefined && Number.isNaN(sb))
        return setError("Starting bid must be a positive number");
      if (rp !== undefined && Number.isNaN(rp))
        return setError("Reserve price must be a positive number");
      if (binChange !== undefined && Number.isNaN(binChange))
        return setError("Buy It Now price must be a positive number");
      const effectiveSb = sb ?? state?.startPrice ?? listing.startingBid;
      if (
        rp !== undefined &&
        !Number.isNaN(rp) &&
        effectiveSb !== undefined &&
        rp < effectiveSb
      ) {
        return setError(
          `Reserve must be at least the starting bid ($${effectiveSb.toFixed(2)})`,
        );
      }
      body = {
        itemId: listing.ebayListingId,
        listingType: "Auction",
        startingBid: sb,
        reservePrice: rp,
        price: binChange,
      };
      appliedSb = sb;
      appliedRp = rp;
      appliedBin = binChange;
    } else {
      const pRaw = parseMoney(bin);
      const p = changed(pRaw, state?.startPrice ?? listing.askingPrice);
      if (p === undefined)
        return setError("Enter a different Buy It Now price");
      if (Number.isNaN(p)) return setError("Enter a valid Buy It Now price");
      body = {
        itemId: listing.ebayListingId,
        listingType: "FixedPrice",
        price: p,
      };
      appliedBin = p;
    }

    setSubmitting(true);
    try {
      await reviseEbayListingPrice(body);
      const patch: Partial<LiveListing> = {};
      if (body.listingType === "FixedPrice") {
        if (appliedBin !== undefined) patch.askingPrice = appliedBin;
      } else {
        if (appliedSb !== undefined) {
          patch.startingBid = appliedSb;
          patch.askingPrice = appliedSb;
        }
        if (appliedRp !== undefined) patch.reservePrice = appliedRp;
        if (appliedBin !== undefined) patch.buyItNowPrice = appliedBin;
      }
      if (Object.keys(patch).length > 0) {
        updateLiveListing({ ...listing, ...patch });
      }
      onSuccess("Price updated on eBay");
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to revise price");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !submitting && onClose()}
      ariaLabel="Edit price"
      panelClassName="w-[min(480px,92vw)] max-h-[85vh] flex flex-col"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">Edit price</h2>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="text-muted-foreground hover:text-foreground disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-4 overflow-y-auto p-4">
        {loading && (
          <p className="text-center text-xs text-muted-foreground">
            Loading current eBay prices…
          </p>
        )}
        {loadError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive bg-destructive/10 p-3 text-xs text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{loadError}</span>
          </div>
        )}
        {!loading && !loadError && (
          <>
            <p className="text-xs text-muted-foreground">
              {isAuction
                ? `Auction · ${state?.bidCount ?? 0} bid${(state?.bidCount ?? 0) === 1 ? "" : "s"}${state?.currentPrice !== undefined ? ` · current $${state.currentPrice.toFixed(2)}` : ""}`
                : `Buy It Now · current $${(state?.startPrice ?? listing.askingPrice).toFixed(2)}`}
            </p>
            {isAuction && caps.lockReason && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/60 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{caps.lockReason}</span>
              </div>
            )}
            {isAuction && (
              <>
                <Field
                  label="Starting bid"
                  value={startingBid}
                  setValue={setStartingBid}
                  enabled={caps.canEditStartingBid}
                  disabledReason="eBay locks the starting bid once bids exist or in the last 12 hours."
                  submitting={submitting}
                />
                <Field
                  label="Reserve price (optional)"
                  value={reserve}
                  setValue={setReserve}
                  enabled={caps.canEditReserve}
                  disabledReason={
                    caps.canEditReserve
                      ? null
                      : state?.bidCount && state.bidCount > 0
                        ? "Reserve can only be lowered after bids — but none is currently set."
                        : "Reserve can't be changed in the last 12 hours."
                  }
                  hint={reserveInputError ?? undefined}
                  hintColor={reserveInputError ? "destructive" : undefined}
                  submitting={submitting}
                />
                <Field
                  label="Buy It Now (optional)"
                  value={bin}
                  setValue={setBin}
                  enabled={caps.canEditBin}
                  disabledReason="Buy It Now is removed once a bid is placed and locked in the final 12 hours."
                  submitting={submitting}
                />
              </>
            )}
            {!isAuction && (
              <Field
                label="Buy It Now price"
                value={bin}
                setValue={setBin}
                enabled
                submitting={submitting}
                autoFocus
              />
            )}
            {error && (
              <div className="flex items-start gap-2 rounded-md border border-destructive bg-destructive/10 p-3 text-xs text-destructive">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="rounded-md bg-muted px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting || loading || !!loadError || !!reserveInputError}
          className="inline-flex items-center gap-1 rounded-md bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? "Saving…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}

function Field({
  label,
  value,
  setValue,
  enabled,
  disabledReason,
  hint,
  hintColor,
  autoFocus,
  submitting,
}: {
  label: string;
  value: string;
  setValue: (s: string) => void;
  enabled: boolean;
  disabledReason?: string | null;
  hint?: string;
  hintColor?: "destructive";
  autoFocus?: boolean;
  submitting: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="0.00"
        disabled={!enabled || submitting}
        autoFocus={autoFocus}
        className={
          enabled
            ? "w-full rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground focus:border-secondary focus:outline-none disabled:opacity-50"
            : "w-full rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground opacity-60"
        }
      />
      {!enabled && disabledReason && (
        <p className="flex items-start gap-1 text-[11px] text-muted-foreground">
          <Lock className="mt-0.5 h-3 w-3 shrink-0" />
          <span className="flex-1">{disabledReason}</span>
        </p>
      )}
      {enabled && hint && (
        <p
          className={
            hintColor === "destructive"
              ? "text-[11px] text-destructive"
              : "text-[11px] text-muted-foreground"
          }
        >
          {hint}
        </p>
      )}
    </div>
  );
}
